"use client";

import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { useAuth } from "@lib/auth/useAuth";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { getValidSaleBadge, isBlackFridayBadge } from "@lib/utils/pdp/saleBadgeUtils";
import type { EccoClubBadge } from "@lib/wrappers/algolia/types";
import { defaultUnderlineStyle } from "@ecco/ui/css/common";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import { Text } from "@ecco/ui/text";
import type { FC, MouseEvent, ReactNode } from "react";

const VARIANTS = {
    pdp: {
        icon: 24,
        gap: "4px",
        mb: 6,
        text: {
            fontSize: { base: "mobileBodyTextNormal", md: "mobileBodyTextLarge" },
            lineHeight: "24px",
        },
    },
    plp: {
        icon: 16,
        gap: "3px",
        mt: "11px",
        pb: "8px",
        text: {
            fontSize: { base: "mobileBodyTextTiny", md: "mobileBodyTextNormal" },
            lineHeight: "tall",
            textUnderlineOffset: "2px",
        },
    },
    cart: {
        icon: 16,
        gap: "3px",
        mt: "11px",
        text: {
            fontSize: { base: "mobileBodyTextTiny", md: "mobileBodyTextNormal" },
            lineHeight: "tall",
            textUnderlineOffset: "2px",
        },
    },
} as const;

export type EccoClubMemberDiscountVariant = keyof typeof VARIANTS;

interface EccoClubMemberDiscountProps {
    badge?: EccoClubBadge;
    variant: EccoClubMemberDiscountVariant;
}

const BoldText: FC<{ children: ReactNode }> = ({ children }) => (
    <Text as="span" fontWeight="bold" fontSize="inherit" lineHeight="inherit">
        {children}
    </Text>
);

export const EccoClubMemberDiscount: FC<EccoClubMemberDiscountProps> = ({ badge, variant }) => {
    const { isLoggedIn, isLoading } = useAuth();
    const { showModal } = useGlobalModalContext();
    const { get: getMicrocopy } = useMicrocopy();
    const { icon, gap, text, ...spacing } = VARIANTS[variant];
    const validBadge = getValidSaleBadge(badge);
    const isBlackFriday = isBlackFridayBadge(validBadge);

    const getMessage = (): ReactNode => {
        if (!validBadge) return null;

        if (isBlackFriday) {
            const blackFridayText = getMicrocopy(CommonCMS.page, CommonCMS.salesDays.blackFriday);
            if (!blackFridayText) return null;

            const extraDiscountText = validBadge.badgeLoggedIn
                ? getMicrocopy(CommonCMS.shared, CommonCMS.extraDiscount, {
                      replacements: { discount: validBadge.badgeLoggedIn },
                  })
                : null;

            return (
                <>
                    <BoldText>{blackFridayText}</BoldText>
                    {extraDiscountText && `: ${extraDiscountText}`}
                </>
            );
        }

        const discount = isLoggedIn
            ? validBadge.badgeLoggedIn || validBadge.badge
            : validBadge.badge || validBadge.badgeLoggedIn;
        if (!discount) return null;

        return getMicrocopy(
            CommonCMS.shared,
            isLoggedIn
                ? CommonCMS.eccoClubMemberDiscountLoggedIn
                : CommonCMS.eccoClubMemberDiscount,
            { replacements: { discount: <BoldText>{discount}</BoldText> }, jsx: true }
        );
    };

    const message = getMessage();
    const opensLogin = !isLoggedIn && !isBlackFriday;

    if (isLoading || !message) return null;

    const openLogin = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        showModal(ModalTypes.login);
    };

    const rowProps = {
        alignItems: "center",
        gap,
        ...spacing,
        w: "100%",
        "data-testid": "ecco-club-member-discount",
    } as const;

    const content = (
        <>
            {!isBlackFriday && (
                <NextImage
                    src="/assets/icons/ecco-club-seal.svg"
                    alt=""
                    width={icon}
                    height={icon}
                    unoptimized={true}
                    w={`${icon}px`}
                    h={`${icon}px`}
                    flexShrink={0}
                />
            )}
            <Text
                as="span"
                flex="1"
                minW={0}
                letterSpacing="0.4px"
                {...(opensLogin && defaultUnderlineStyle)}
                {...text}
            >
                {message}
            </Text>
        </>
    );

    if (!opensLogin) {
        return <Flex {...rowProps}>{content}</Flex>;
    }

    return (
        <Flex
            as="button"
            type="button"
            onClick={openLogin}
            cursor="pointer"
            p={0}
            textAlign="left"
            bg="transparent"
            border="0"
            color="inherit"
            transition="color 0.2s ease-out"
            _hover={{ color: "gray.500" }}
            sx={{ font: "inherit" }}
            {...rowProps}
        >
            {content}
        </Flex>
    );
};
